// src/utils/menuItemHelpers.ts

import type {
	BranchActionConfig,
	MenuItem,
	VariantComboData,
	VariantOptionGroup,
	VariantOptionValue,
} from "~/types/new_types";
import { foodTags } from "./foodTags";

/**
 * Normalize a menu item from the database format
 * Since we now use simplified structure, this is a pass-through function
 * @param item - Raw menu item from database
 * @returns Normalized menu item
 */
export const normalizeMenuItem = (item: any): MenuItem => {
	// With the simplified structure, we can return the item as-is
	// The database should already contain the correct format
	return item as MenuItem;
};


/**
 * Get the localized name for a menu item based on current language
 * @param item - The menu item object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized menu item name
 */
export const getItemName = (item: MenuItem, language: string): string => {
	if (language === 'ar' && item.ar?.name) {
		return item.ar.name;
	}
	return item.name || '';
};

/**
 * Get the localized description for a menu item based on current language
 * @param item - The menu item object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized menu item description
 */
export const getItemDescription = (item: MenuItem, language: string): string | null => {
	if (language === 'ar' && item.ar?.description) {
		return item.ar.description;
	}
	return item.description || null;
};

/**
 * Get the localized ingredients for a menu item based on current language
 * @param item - The menu item object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized menu item ingredients array
 */
export const getItemIngredients = (item: MenuItem, language: string): string[] => {
	if (!item.ingredients || item.ingredients.length === 0) {
		return [];
	}
	
	if (language === 'ar') {
		return item.ingredients.map(ingredient => ingredient.ar || ingredient.name);
	}
	
	return item.ingredients.map(ingredient => ingredient.name);
};

export const itemIs = (item: MenuItem, label: string) => {
	const tagIndex = foodTags.indexOf(label);
	if (tagIndex === -1) {
		return false;
	}

	const tags = item.tags as number[] | undefined;
	if (!tags) {
		return false;
	}

	return tags.includes(tagIndex);
};

/**
 * Check if menu item has Arabic translation
 * @param item - The menu item object
 * @returns Boolean indicating whether the item has any Arabic content
 */
export const hasArabicTranslation = (item: MenuItem): boolean => {
	// Check for Arabic name and description
	const hasArabicText = !!(item.ar?.name || item.ar?.description);
	
	// Check for Arabic ingredients
	const hasArabicIngredients = !!(item.ingredients && item.ingredients.some(ingredient => ingredient.ar));
	
	return hasArabicText || hasArabicIngredients;
};

const toNumeric = (value: unknown, fallback = 0): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === "string") {
		const numeric = Number(value);
		if (Number.isFinite(numeric)) {
			return numeric;
		}
	}
	return fallback;
};

const coerceBoolean = (value: unknown, fallback = false): boolean => {
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value === "number") {
		return value !== 0;
	}
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (["true", "1", "yes", "y", "on"].includes(normalized)) {
			return true;
		}
		if (["false", "0", "no", "n", "off"].includes(normalized)) {
			return false;
		}
	}
	return fallback;
};

// Helper function to get localized form name
export const getFormName = (group: any, language: string): string => {
	if (language === "ar" && group?.arabic_form_name?.form_name) {
		return group.arabic_form_name.form_name;
	}
	return group?.form_name || "";
};

// Helper function to get variant group key
export const getVariantGroupKey = (group: any): string => {
	if (group?.id) return group.id;
	if (group?.form_name) return group.form_name;
	return "unknown";
};

// Helper function to get variant option name  
export const getVariantOptionName = (option: any): string => {
	return option?.name || "Unknown";
};

// Helper function to get variant option price
export const getVariantOptionPrice = (option: any): number => {
	return typeof option?.price === 'number' ? option.price : 0;
};

export const getOptionStorageKey = (option: any): string => {
	if (!option || typeof option !== "object") {
		return "unknown-option";
	}

	const extractCandidate = (candidate: unknown): string | undefined => {
		if (typeof candidate === "string" && candidate.trim()) {
			return candidate.trim();
		}
		return undefined;
	};

	const possibleKeys: Array<unknown> = [
		(option as { id?: unknown }).id,
		(option as { form_name?: unknown }).form_name,
		(option as { formName?: unknown }).formName,
		(option as { storageKey?: unknown }).storageKey,
		(option as { key?: unknown }).key,
		(option as { name?: unknown }).name,
		(option as { label?: unknown }).label,
	];

	for (const value of possibleKeys) {
		const candidate = extractCandidate(value);
		if (candidate) {
			return candidate;
		}
	}

	return "unknown-option";
};

/**
 * Represents a selected variant for direct add-to-cart
 */
export type DirectAddVariant = {
	optionName: string;
	variantName: string;
	variantPrice: number;
	isVariant: boolean;
	isMultiple: boolean;
	variantType: string;
	actionType: string;
	quantity: number;
};

/**
 * Result from getDirectAddPayload
 * - If null: item requires manual selection (open modal)
 * - If object: item can be added directly with these variants
 */
export type DirectAddPayload = {
	variants: DirectAddVariant[];
} | null;

/**
 * Attempts to build a direct add-to-cart payload using default variant selections.
 * 
 * Logic:
 * - If item has meals_and_combos, return null (always needs modal)
 * - For each variant group:
 *   - If required (min_selection > 0) and has a default option, select it
 *   - If required but NO default, return null (needs modal)
 *   - If optional, skip (don't auto-add optional extras)
 * 
 * @param item - The menu item to check
 * @returns DirectAddPayload or null if manual selection is needed
 */
export const getDirectAddPayload = (item: MenuItem): DirectAddPayload => {
	// If item has combos, always require modal (too complex)
	if (item.meals_and_combos && item.meals_and_combos.length > 0) {
		return null;
	}

	// If no variants at all, can add directly with empty variants
	if (!item.variants_and_options || item.variants_and_options.length === 0) {
		return { variants: [] };
	}

	const selectedVariants: DirectAddVariant[] = [];

	for (const group of item.variants_and_options) {
		// Check if this group is required
		const minSelection = typeof group.min_selection === 'number' 
			? group.min_selection 
			: typeof group.min_selection === 'string' 
				? parseInt(group.min_selection, 10) || 0 
				: 0;

		const isRequired = minSelection > 0;

		// Get the options array
		const options = Array.isArray(group.data) ? group.data : [];

		if (isRequired) {
			// Find a default option
			const defaultOption = options.find(opt => opt.is_default === true);

			if (defaultOption) {
				// Use the default option
				selectedVariants.push({
					optionName: group.form_name || 'Options',
					variantName: defaultOption.name,
					variantPrice: typeof defaultOption.price === 'number' 
						? defaultOption.price 
						: typeof defaultOption.price === 'string' 
							? parseFloat(defaultOption.price) || 0 
							: 0,
					isVariant: true,
					isMultiple: !!group.is_multiselection,
					variantType: 'variants_and_options',
					actionType: group.action_type || 'selection',
					quantity: 1,
				});
			} else {
				// Required group but no default - can't add directly
				return null;
			}
		}
		// If optional, we skip (don't auto-add optional extras)
	}

	return { variants: selectedVariants };
};